import HomeLayout from "./layout/HomeLayout.tsx";
import HomeDashboard from "./dashboard/Home.tsx";
import MyLibrary from "./my-library/MyLibrary.tsx";
import { redeemCodesAction } from "./actions/redeem/redeem.ts";
import InsetHomeError from "./InsetHomeError.tsx";
import Expired from "./expired/Expired.tsx";
import Favorites from "./favorites/Favorites.tsx";
import { revealCode } from "./actions/courseware/courseware.ts";
import { searchRoutes } from "./search/searchRoutes.tsx";
import { dashboardLoader } from "./dashboard/dashboardLoader.ts";
import {
  libraryLoader,
  loadRecentExploreAssets,
} from "./my-library/libraryLoader.ts";
import { expiredLoader } from "./expired/expiredLoader.ts";
import {
  getDynamicLink,
  updateUser,
  updateUserPreference,
} from "./actions/user/user.ts";
import { returnAction } from "./actions/explore/return/return.ts";
import { HomeRouteHandle } from "./homeRouteHandle.ts";
import { activateAction } from "./actions/explore/activate/activate.ts";
import { homeRouteId, loadHomeData } from "./homeContext.ts";
import { redeemLoader } from "./redeem/redeemLoader.ts";
import RedeemMain from "./redeem/RedeemMain.tsx";
import { favorites } from "./actions/favorites/favorites.ts";
import { favoritesLoader } from "./favorites/favorites.ts";
import { subscribeLoader } from "./subscribe/subscribeLoader.ts";
import SubscribeMain from "./subscribe/SubscribeMain.tsx";
import Admin from "./admin/Admin.tsx";
import Groups from "./groups/Groups.tsx";
import exploreRedirector from "./common/explore/exploreRedirect.ts";
import myCoursesRoutes from "./my-courses/myCoursesRoutes.tsx";
import { booksRouteLoader } from "./books/booksRouteLoader.ts";
import AssetActivator from "./activate/AssetActivator.tsx";
import { activateLoader } from "./activate/activateLoader.ts";
import { ShouldRevalidateFunctionArgs } from "react-router-dom";
import { updateLibraryAction } from "./actions/update-library/updateLibrary.ts";
import { expiredDataLoader } from "./data/expired/expiredDataLoader.ts";
import {
  promotionActivateAction,
  promotionReturnAction,
} from "./actions/explore-promotion/promotions.ts";
import { exploreRoutes } from "./explore-switcher/exploreRoutes.tsx";
import { getInstructorCoursesLoader } from "./manage-engagement-score/manageEngagementScoreLoader.ts";
import { recommendationsRoutes } from "./recommendations/recommendationsRoutes.ts";
import { myCartRoutes } from "./my-cart/myCartRoutes.ts";
import OrderHistory from "./order-history/OrderHistory.tsx";
import { productRoutes } from "./product/productRoutes.ts";

export const homeRoutes = {
  id: homeRouteId,
  loader: loadHomeData,
  // shouldRevalidate must be false because parent revalidation blocks navigation
  // and it's very slow. See HomeContextProvider etc for revalidation.
  shouldRevalidate: () => false,
  children: [
    {
      element: <HomeLayout />,
      children: [
        {
          errorElement: <InsetHomeError />,
          children: [
            {
              path: "/dashboard",
              element: <HomeDashboard />,
              loader: dashboardLoader,
            },
            { ...myCoursesRoutes },
            { ...exploreRoutes },
            {
              path: "/classes",
              element: <Groups />,
            },
            {
              path: "/classes/*",
              loader: exploreRedirector,
            },
            {
              path: "/admin",
              element: <Admin />,
            },
            {
              path: "/admin/*",
              loader: exploreRedirector,
            },
            {
              path: "/my-library",
              element: <MyLibrary />,
              // Does not have its own loader because it is preferable for all pages
              // to load in the exact same way
              children: [
                { path: "/my-library/assets/:page", loader: libraryLoader },
                {
                  path: "/my-library/recent-explore-assets",
                  loader: loadRecentExploreAssets,
                },
              ],
            },
            {
              path: "/expired",
              element: <Expired />,
              // Does not have its own loader because it is preferable for all pages
              // to load in the exact same way
              children: [
                { path: "/expired/assets/:page", loader: expiredLoader },
              ],
            },
            {
              path: "/favorites",
              element: <Favorites />,
              loader: favoritesLoader,
              shouldRevalidate: (args: ShouldRevalidateFunctionArgs) => {
                // if on favorites, clicking the same link to favorites (i.e. stay on favorites)
                // caused the loader to run again, unnecessary and interfered with a re-focus rect
                // but we do want revalidation for say a DELETE etc
                return !!args.formMethod;
              },
            },
            recommendationsRoutes,
            myCartRoutes,
            productRoutes,
            { ...searchRoutes },
            {
              path: "/manage-engagement-score",
              async lazy() {
                const { manageEngagementScore } = await import(
                  "./manage-engagement-score/lazyRoute.ts"
                );
                return manageEngagementScore;
              },
            },
            {
              path: "/manage-engagement-score/courses/:sessionId",
              loader: getInstructorCoursesLoader,
              shouldRevalidate: () => false,
            },
            {
              path: "/manage-engagement-score/:page/:vbid/:courseId",
              async lazy() {
                const { engagementScoreEmbed } = await import(
                  "./manage-engagement-score/lazyRoute.ts"
                );
                return engagementScoreEmbed;
              },
            },
            // Just an iframe, don't need to lazy load
            { path: "/order-history", element: <OrderHistory /> },
            // Related to fraction route /#/activate/:vbid
            {
              path: "/activate/:vbid/*",
              loader: activateLoader,
              element: <AssetActivator />,
              handle: { sidebar: false } as HomeRouteHandle,
            },
            {
              // Legacy fraction route, /#/books/:vbid/.*
              path: "/books/:vbid?/*",
              loader: booksRouteLoader,
            },
            {
              // Legacy fraction route, /#/home/redeem/:code
              // Optional /test segment assists with testing outside the /home route overload
              path: "/test?/redeem/:code",
              loader: redeemLoader,
              element: <RedeemMain />,
            },
            {
              // Legacy fraction route, /#/home/subscribe/:email
              // Optional /test segment assists with testing outside the /home route overload
              path: "/test?/subscribe/:email",
              loader: subscribeLoader,
              element: <SubscribeMain />,
            },
          ],
        },
      ],
    },
    /**
     * This section is for general actions that may be called on a variety of pages.
     * Page-specific actions should be co-located with the relevant page
     */
    {
      path: "/actions",
      children: [
        // VST Codes
        { path: "/actions/redeem", action: redeemCodesAction },
        // Third-party courseware
        { path: "/actions/reveal/:vbid", action: revealCode },
        // Language preference update
        { path: "/actions/user", action: updateUser },
        // User Preferences
        {
          path: "/actions/user/preferences/:id",
          action: updateUserPreference,
        },
        // QR Code
        {
          path: "/actions/user/dynamic-link",
          loader: getDynamicLink,
          shouldRevalidate: () => false,
        },
        // Explore book returns
        { path: "/actions/return/:vbid", action: returnAction },
        // Explore book borrows
        { path: "/actions/activate/:vbid", action: activateAction },
        // Favorites
        {
          path: "/actions/user/favorites",
          action: favorites,
        },
        {
          path: "/actions/update-library",
          action: updateLibraryAction,
        },
        // activate a promotion subscription (e.g. free books)
        {
          path: "/actions/promotion/activate/:vbid",
          action: promotionActivateAction,
        },
        // return a promotion subscription (e.g. free books)
        {
          path: "/actions/promotion/return/:vbid",
          action: promotionReturnAction,
        },
      ],
    },
    /**
     * This section is for general non-rendered fetches that may be called on a variety of pages.
     * Page-specific loads should be co-located with the relevant page
     */
    {
      path: "/data",
      children: [
        // More nuanced expired (e.g. recently expired)
        {
          path: "/data/expired/:page",
          loader: expiredDataLoader,
          // Used in sidebar, can't revalidate or it blocks navigation
          shouldRevalidate: () => false,
        },
      ],
    },
  ],
};
